import { createSlice, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import {
  convertExternalAppointment,
  deleteCalendarEvents,
  fetchCalendarEvents,
  getCalendarEvent,
  syncBreaksToDiwa,
  searchCalendarEvents,
  updateCalendarEvents, createCalendarEvents,
  updateCalendarEventNote,
} from './thunks';
import { Appointment, CalendarEventTypeEnum, Employee, ExternalAppointment, CalendarEventSearchItem } from '../../types';
import { getSplitServiceAppointments } from '../../utils';

interface InitialState {
  loading: boolean;
  error: any;
  singleAppointment: Appointment | null;
  data: Appointment[];
  search: {
    data: CalendarEventSearchItem[] | null;
    loading: boolean;
    error: SerializedError | null;
    hasNextPage: boolean;
  }
  createCalendarEvent: {
    data: Appointment | null;
    loading: boolean;
    error: SerializedError | null;
  },
  updateCalendarEvent: {
    data: Appointment | null;
    loading: boolean;
    error: SerializedError | null;
  },
  deleteCalendarEvent: {
    loading: boolean;
    error: SerializedError | string | null;
    savedAt: number | null;
  },
  updateCalendarEventNote: {
    loading: boolean;
    error: SerializedError | null;
    requestId: string | null;
    savedAt: number | null;
    previousNote: string | null;
    appointmentId: string | null;
  },
}

const initialState: InitialState = {
  loading: false,
  error: null,
  singleAppointment: null,
  data: [],
  search: {
    data: null,
    loading: false,
    error: null,
    hasNextPage: true,
  },
  createCalendarEvent: {
    data: null,
    loading: false,
    error: null,
  },
  updateCalendarEvent: {
    data: null,
    loading: false,
    error: null,
  },
  deleteCalendarEvent: {
    loading: false,
    error: null,
    savedAt: null,
  },
  updateCalendarEventNote: {
    loading: false,
    error: null,
    requestId: null,
    savedAt: null,
    previousNote: null,
    appointmentId: null,
  },
};

const slice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    addCalendarEvent: (state, action) => {
      const data = getSplitServiceAppointments([action.payload.data], action.payload.timezone);

      state.data = state.data.concat(data.map((calendarEvent) => convertExternalAppointment(calendarEvent, action.payload.timezone)));
    },
    updateCalendarEvent: (state, action) => {
      const updatedCalendarEvent = convertExternalAppointment(action.payload.data, action.payload.timezone);
      const calendarEventId = updatedCalendarEvent.id;
      const updatedData = state.data.filter((event) => event.id !== calendarEventId);

      const data = getSplitServiceAppointments([updatedCalendarEvent], action.payload.timezone);

      updatedData.push(...data);
      state.data = updatedData;

      if (state.search.data && state.search.data.length > 0 && updatedCalendarEvent.type === CalendarEventTypeEnum.APPOINTMENT) {
        const updatedSearchData = state.search.data.filter((event) => event.id !== calendarEventId);
        updatedSearchData.push(updatedCalendarEvent);
        state.search.data = updatedSearchData;
      }
    },
    deleteCalendarEvent: (state, action) => {
      const calendarEventId = action.payload.id;
      const updatedData = state.data.filter((event) => event.id !== calendarEventId);
      if (updatedData.length !== state.data.length) {
        state.data = updatedData;
      }
      if (state.search.data && state.search.data.length > 0) {
        const updatedSearchData = state.search.data.filter((event) => event.id !== calendarEventId);
        if (updatedSearchData.length !== state.search.data.length) {
          state.search.data = updatedSearchData;
        }
      }
    },
    syncCalendarEvents: (
      state,
      action: {
        payload: {
          data: {
            create: ExternalAppointment[];
            update: ExternalAppointment[];
            delete: Appointment[];
          };
          timezone: string;
        };
      },
    ) => {
      const createsById: Record<string, Appointment> = {};
      const updatesById: Record<string, Appointment> = {};
      const deletesById: Record<string, Appointment> = {};

      const updatedData: Appointment[] = [];

      const createsItemsLength = action.payload.data.create.length;
      for (let itemIndex = 0; itemIndex < createsItemsLength; itemIndex += 1) {
        const dataItem = action.payload.data.create[itemIndex];
        const appointment = convertExternalAppointment(dataItem, action.payload.timezone);
        createsById[dataItem.id] = appointment;

        const singleOrSplitAppointments = getSplitServiceAppointments([appointment], action.payload.timezone);
        updatedData.push(...singleOrSplitAppointments);
      }

      const updatedItemsLength = action.payload.data.update.length;
      for (let itemIndex = 0; itemIndex < updatedItemsLength; itemIndex += 1) {
        const dataItem = action.payload.data.update[itemIndex];
        const appointment = convertExternalAppointment(dataItem, action.payload.timezone);
        updatesById[dataItem.id] = appointment;

        const singleOrSplitAppointments = getSplitServiceAppointments([appointment], action.payload.timezone);
        updatedData.push(...singleOrSplitAppointments);
      }

      const deletedItemsLength = action.payload.data.delete.length;
      for (let itemIndex = 0; itemIndex < deletedItemsLength; itemIndex += 1) {
        const appointment = action.payload.data.delete[itemIndex];
        deletesById[appointment.id] = appointment;
      }

      const filteredData = state.data.filter((item) => !createsById[item.id] && !updatesById[item.id] && !deletesById[item.id]);

      state.data = filteredData.concat(updatedData);
    },
    resetSingleAppointment(state) {
      state.singleAppointment = null;
    },
    resetCreateCalendarEvent(state) {
      state.createCalendarEvent = initialState.createCalendarEvent;
    },
    resetUpdateCalendarEvent(state) {
      state.updateCalendarEvent = initialState.updateCalendarEvent;
    },
    resetUpdateCalendarEventNote(state) {
      state.updateCalendarEventNote = initialState.updateCalendarEventNote;
    },
    resetSearch(state) {
      state.search = { ...initialState.search };
    },
    updateSearchDataEmployees(state, action: PayloadAction<{ employees: Employee[] }>) {
      const employeesByIdMap = action.payload.employees.reduce<Record<string, Employee>>((acc, employee) => {
        acc[employee.id] = employee;
        return acc;
      }, {});

      if (state.search.data) {
        const updatedSearchData = [...state.search.data];

        for (let searchItemIndex = 0; searchItemIndex < updatedSearchData.length; searchItemIndex += 1) {
          const searchItem = updatedSearchData[searchItemIndex];
          if (searchItem.pinnedEmployee && !searchItem.employee) {
            const employee = employeesByIdMap[searchItem.pinnedEmployee];
            if (employee) {
              updatedSearchData[searchItemIndex] = {
                ...searchItem,
                employee: {
                  id: employee.id,
                  name: employee.name,
                  firstName: employee.firstName,
                  lastName: employee.lastName,
                },
              };
            }
          }
        }

        state.search.data = updatedSearchData;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCalendarEvents.pending, (state) => {
      state.loading = true;
      state.error = null;
      // state.data = []; //todo
    });
    builder.addCase(fetchCalendarEvents.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    });

    builder.addCase(createCalendarEvents.pending, (state) => {
      state.createCalendarEvent.data = null;
      state.createCalendarEvent.loading = true;
      state.createCalendarEvent.error = null;
    });
    builder.addCase(createCalendarEvents.fulfilled, (state, action) => {
      state.createCalendarEvent.data = action.payload;
      state.createCalendarEvent.loading = false;
    });
    builder.addCase(createCalendarEvents.rejected, (state, action) => {
      state.createCalendarEvent.loading = false;
      state.createCalendarEvent.error = action.error;
    });

    builder.addCase(updateCalendarEvents.pending, (state) => {
      state.updateCalendarEvent.data = null;
      state.updateCalendarEvent.loading = true;
      state.updateCalendarEvent.error = null;

      state.error = null;
    });
    builder.addCase(updateCalendarEvents.fulfilled, (state, action) => {
      state.updateCalendarEvent.data = action.payload;
      state.updateCalendarEvent.loading = false;

      state.error = null;

      if (state.search.data && state.search.data.length) {
        const searchItemByIdMap = state.search.data.reduce((acc, appointment) => {
          acc[appointment.id] = appointment;
          return acc;
        }, {});

        if (searchItemByIdMap[action.payload.id]) {
          searchItemByIdMap[action.payload.id] = action.payload;
        }

        state.search.data = Object.values(searchItemByIdMap);
      }
    });
    builder.addCase(updateCalendarEvents.rejected, (state, action) => {
      state.updateCalendarEvent.loading = false;
      state.updateCalendarEvent.error = action.error;
    });

    builder.addCase(updateCalendarEventNote.pending, (state, action) => {
      const { appointmentId, note } = action.meta.arg;

      let previousNote: string | null = null;
      for (let dataIndex = 0; dataIndex < state.data.length; dataIndex += 1) {
        const event = state.data[dataIndex];
        if (event.id === appointmentId) {
          if (previousNote === null) {
            previousNote = event.note === undefined ? '' : event.note;
          }
          state.data[dataIndex] = { ...event, note };
        }
      }

      state.updateCalendarEventNote.loading = true;
      state.updateCalendarEventNote.error = null;
      state.updateCalendarEventNote.requestId = action.meta.requestId;
      state.updateCalendarEventNote.savedAt = null;
      state.updateCalendarEventNote.previousNote = previousNote;
      state.updateCalendarEventNote.appointmentId = appointmentId;
    });
    builder.addCase(updateCalendarEventNote.fulfilled, (state, action) => {
      if (state.updateCalendarEventNote.requestId !== action.meta.requestId) {
        return;
      }

      const updated = action.payload;
      for (let dataIndex = 0; dataIndex < state.data.length; dataIndex += 1) {
        const event = state.data[dataIndex];
        if (event.id === updated.id) {
          state.data[dataIndex] = { ...event, note: updated.note };
        }
      }

      state.updateCalendarEventNote.loading = false;
      state.updateCalendarEventNote.error = null;
      state.updateCalendarEventNote.savedAt = Date.now();
      state.updateCalendarEventNote.previousNote = null;
      state.updateCalendarEventNote.appointmentId = null;
    });
    builder.addCase(updateCalendarEventNote.rejected, (state, action) => {
      if (state.updateCalendarEventNote.requestId !== action.meta.requestId) {
        return;
      }

      const { appointmentId } = action.meta.arg;
      const { previousNote } = state.updateCalendarEventNote;
      if (previousNote !== null) {
        for (let dataIndex = 0; dataIndex < state.data.length; dataIndex += 1) {
          const event = state.data[dataIndex];
          if (event.id === appointmentId) {
            state.data[dataIndex] = { ...event, note: previousNote };
          }
        }
      }

      state.updateCalendarEventNote.loading = false;
      state.updateCalendarEventNote.error = action.error;
      state.updateCalendarEventNote.savedAt = null;
      state.updateCalendarEventNote.previousNote = null;
      state.updateCalendarEventNote.appointmentId = null;
    });

    builder.addCase(deleteCalendarEvents.pending, (state) => {
      state.deleteCalendarEvent.loading = true;
      state.deleteCalendarEvent.error = null;
    });
    builder.addCase(deleteCalendarEvents.fulfilled, (state, action) => {
      state.deleteCalendarEvent.loading = false;

      if (action.payload?.error) {
        state.deleteCalendarEvent.error = action.payload.error.message || 'deletion error';
        state.deleteCalendarEvent.savedAt = null;
      } else {
        state.deleteCalendarEvent.error = null;
        state.deleteCalendarEvent.savedAt = Date.now();
      }
    });
    builder.addCase(deleteCalendarEvents.rejected, (state, payload) => {
      state.deleteCalendarEvent.loading = false;
      state.deleteCalendarEvent.error = payload?.error ?? null;
      state.deleteCalendarEvent.savedAt = null;
    });

    builder.addCase(syncBreaksToDiwa.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(syncBreaksToDiwa.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(syncBreaksToDiwa.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(searchCalendarEvents.pending, (state) => {
      state.search.loading = true;
      state.error = null;
    });
    builder.addCase(searchCalendarEvents.fulfilled, (state, action) => {
      state.search.loading = false;
      state.error = null;
      state.search.hasNextPage = action.payload.length >= action.meta.arg.itemsPerPage;
      const searchResults = action.payload.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
      if (action.meta.arg.page === 1) {
        state.search.data = action.payload;
      } else {
        const currentData = state.search.data || [];
        state.search.data = currentData.filter((event) => !searchResults[event.id]).concat(action.payload);
      }
    });
    builder.addCase(searchCalendarEvents.rejected, (state, action) => {
      state.search.loading = false;
      state.search.hasNextPage = false;
      state.error = action.error;
    });

    builder.addCase(getCalendarEvent.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getCalendarEvent.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.singleAppointment = action.payload;
    });
    builder.addCase(getCalendarEvent.rejected, (state, action) => {
      state.loading = false;
      state.singleAppointment = null;
      state.error = action.error;
    });

    builder.addCase(fetchCalendarEvents.rejected, (state, payload) => {
      state.loading = false;
      state.error = payload?.error;
    });
  },
});

export const {
  addCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  syncCalendarEvents,
  resetSingleAppointment,
  resetSearch,
  updateSearchDataEmployees,
  resetCreateCalendarEvent,
  resetUpdateCalendarEvent,
  resetUpdateCalendarEventNote,
} = slice.actions;
export default slice.reducer;
